import {computed, ref, useI18n, useRoute} from "#imports";
import {applicationContext} from "@@/package/application/applicationContext";
import {useLearningStore} from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import {useNuxtApp} from "#app";
import {TaskType} from "~~/package/mapper/learning/model/TaskType";
import {CodeRunModel, ErrorType} from "~~/package/mapper/learning/model/CodeRunModel";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import LearningConfirmPopupType from "~/components/Common/Dialog/dialog/LearningConfirmPopup/LearningConfirmPopupType";
import {useLevelAssessmentService} from "~/composables/service/useLevelAssessmentService";
import {useUserStore} from "~/store/user/userStore";
import {useDialogStore} from "~/store/dialog/dialogStore";
import {useLearningService} from "~/composables/service/useLearningService";
import {useLearningDirectoryService} from "~/composables/service/useLearningDirectoryService";
import type {Ref, UnwrapRef} from "vue";
import {useProSkillAssureService} from "~/composables/service/useProSkillAssureService";
import {
    ProSkillAssureAnswerCodeModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeModel";
import {
    ProSkillAssureAnswerCodeSubmitType
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeSubmitType";
import {
    ProSkillAssureAnswerCodeSubmitModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAnswerCodeSubmitModel";
import LearningMenuType from "~/components/Learning/LearningMiddle/config/LearningMenuType";
import {useWebContainerService} from "~/composables/service/useWebContainerService";
import {LearningTokenData} from "~~/package/mapper/learning/model/LearningTokenData";
import {utf8ToBase64} from "~~/package/utils/Base64Util";
import {useToastStore} from "~/store/toast/toastStore";
import {ToastType} from "~/components/Common/Toast/model/ToastType";
import {useExamSession} from "~/composables/useExamSession";

export const useLearningRunCodeService = () => {
    const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
    const config = { public: applicationContext.getConfig() };
    const route = useRoute();
    const userStore = useUserStore();
    const dialogStore = useDialogStore();
    const toastStore = useToastStore();
    const {getToken, saveCode, saveUserCode} = useLearningService();
    const learningDirectoryService = useLearningDirectoryService();
    const webContainerService = useWebContainerService();
    const proSkillAssureService = useProSkillAssureService();
    const {$i18n} = useNuxtApp();
    const t = $i18n.t.bind($i18n);
    const lang = computed(() =>
        $i18n.locale.value.toUpperCase()
    );

    const connectFailCount = ref(0);
    const  domainData = computed(() => {
        if (learningStore.isLevelAssessment) return {domain: 'level_assessment', domainId: parseInt(route.params.levelId as string)}
        else if (learningStore.isProSkillAssure) return {domain: 'custom_assessment', domainId: assessmentStore.proSkillAssure.assessmentId}
        else if (learningStore.isContest) return {domain: 'contest', domainId: assessmentStore.contestId}
        else if (learningStore.isLearning) return {domain: 'training', domainId: learningStore.course.id as number}
        return {domain: "unknown", domainId: 0}
    });

    const socket: Ref<UnwrapRef<WebSocket | null>> = ref(null);

    const baseUrl = computed(() => {
        return `${config.public.CODE_RUN_BRIDGE_SOCKET_URL}?token=${learningStore.tokenData.token}&locale=${lang.value}`;
    });

    const onClickRun = () => {
        // #2260: the exam session expired — run needs auth (token, save, RUN record). Send nothing,
        // stay out of the running state, and point the candidate at the login popup again.
        const examSession = useExamSession();
        if (examSession.isExpired()) {
            examSession.promptRelogin();
            return;
        }
        if(learningStore.isFrontendLearning) frontendCodeRun();
        else defaultCodeRun();
    }

    const socketOnClose = () => {
        if (!learningStore.socket) return;

        learningStore.socket.close();
        learningStore.isRunning = false;
    }

    const frontendCodeRun = (isReload = false) => {
        learningDirectoryService.saveMultipleCode(isReload, true); // save 저장

        learningStore.isFrontRun = true;
        if(learningStore.frontLearning.defaultLocation.length !== 0) {
            if (isReload) {
                learningStore.directory.modifiedFiles.map(modifiedFile => {
                    webContainerService.writeModifiedFile(modifiedFile.path, modifiedFile.file.file.contents).then(() => {
                        learningStore.isFrontRun = false;
                    });
                });
            }
            else webContainerService.runWebContainer(true);

            learningDirectoryService.saveMultipleCode(isReload); // run_code 저장

            if(learningStore.menuType === LearningMenuType.Description) {
                if(!learningStore.frontLearning.isShowToastMessage && !learningStore.menuActiveList.includes(LearningMenuType.Preview)) {
                    learningStore.frontLearning.isShowToastMessage = true;
                    toastStore.openToast({
                        toastType: ToastType.INFO,
                        contents: t("learning-front-preview-guide"),
                    });
                }

                learningStore.menuActiveList = [...new Set([...learningStore.menuActiveList, LearningMenuType.Preview])];
            } else {
                learningStore.menuType = LearningMenuType.Preview;
                learningStore.menuActiveList = learningStore.menuActiveList.filter(menu => menu !== LearningMenuType.Preview);
            }

            learningStore.isNeverRun = false;
        }
    }

    const defaultCodeRun = () => {
        learningStore.isUpdateCode = false;
        if(learningStore.task.type === TaskType.MULTI_FILE_CODE) {
            learningDirectoryService.saveMultipleCode(false, true);
            learningDirectoryService.saveMultipleCode(false, false, false, () => runCodeHandler());
        } else {
            if (learningStore.task.type !== TaskType.ACTIVITY_CODE && !learningStore.isValidate) saveCodeOnly();
            runCodeHandler();
        }
    }

    // ASMT-CAND-11: Run code — 코드 실행 이력 기록 후 coderun bridge(/global WS)로 실행, Output 수신
    const runCodeHandler = () => {
        learningStore.runTraining();

        initToken().then(() => {
            if (learningStore.isLevelAssessment) submitLevelAssessmentCurrentCode();
            else if (learningStore.isProSkillAssure) submitProSkillAssureCurrentCode();
            else if (learningStore.isContest) learningStore.updateContestList();
            else if (!(learningStore.isAssessment || learningStore.isValidate)) saveHistory();
            connectServer()
        }).catch((e: unknown) => {
            // #2260: the session died mid-run (exam screen) — leave the running state so the button
            // isn't stuck; the session-expired popup takes over. Other failures behave as before.
            if (!useExamSession().handleUnauthorized(e)) throw e;
            learningStore.isRunning = false;
        })
    }

    const saveCodeOnly = (isSubmission = false) => {
        const taskId = learningStore.task.id;
        // 성공 시점이 아니라 지금 읽는다 — 응답 전에 다음 문항 진입이 이 플래그를 끈다.
        const hasUnsubmittedEdit = learningStore.isChangedButNotSubmit;
        saveUserCode(
            domainData.value.domain,
            domainData.value.domainId,
            learningStore.convertedCurrentCode,
        ).then(response => {
            if (response) {
                learningStore.updateContestList();
                if (!isSubmission && hasUnsubmittedEdit) learningStore.unsubmittedChangeByTaskId[taskId] = true;
            }
            if (!isSubmission) learningStore.isSaved = response;
        }).catch((e: unknown) => {
            // #2260: a dead exam session shows the session-expired popup; other failures as before.
            if (!useExamSession().handleUnauthorized(e)) throw e;
        });
    }

    const connectServer = () => {
        if (socket.value == null || socket.value.readyState !== WebSocket.OPEN) {
            socket.value = new WebSocket(baseUrl.value);
            socket.value.onmessage = socketOnMessage;
            socket.value.onopen = socketOnOpen;
            socket.value.onerror = socketOnError;
            learningStore.socket = socket.value;
        } else {
            sendMessage();
        }
    }

    const saveHistory = () => {
        saveCode();
    }

    const initToken = () => {
        if (!learningStore.tokenData.token || !learningStore.tokenData.hmac) {
            return getToken(lang.value)
                .then((tokenData: LearningTokenData) => learningStore.updateToken(tokenData));
        } else {
            return Promise.resolve()
        }
    }

    const socketOnOpen = () => {
        sendMessage();
    }

    const socketOnMessage = (ev: MessageEvent) => {
        try {
            const codeRun = CodeRunModel.create(JSON.parse(ev.data));
            // GA error 관리 다국어 적용 시 outputDescription 수정 필요
            if (codeRun.errorType === ErrorType.SERVER_ERROR) {
                handleSocketError();
            } else {
                learningStore.addTrainingData(codeRun.output, codeRun.byteImageData, codeRun.error, codeRun.errorGuide, codeRun.end, codeRun.validSet, codeRun.score);
                learningStore.isNeverRun = false;
            }
        } catch (error) {
        }
    }

    const sendMessage = () => {
        const message = {
            userId: userStore.userV2.id,
            courseId: parseInt(route.params.courseId as string),
            subChapterId: parseInt(route.params.subChapterId as string),
            langType: learningStore.task.langType,
            code: learningStore.convertedCurrentCode,
            testCaseType: "NORMAL",
            taskType: learningStore.task.type,
            domain: domainData.value.domain,
            domainId: domainData.value.domainId,
            userUUID: userStore.userV2.uuid
        }
        socket.value?.send(JSON.stringify(message));
    }

    const socketOnError = () => {
        connectFailCount.value += 1;
        if (connectFailCount.value < 30) {
            setTimeout(connectServer, 10000);
        } else {
            handleSocketError();
        }
    }
    const handleSocketError = () => {
        connectFailCount.value = 0;
        learningStore.addTrainingData("", [], "", "", true, "", undefined);
        learningStore.updateToken(new LearningTokenData());
        dialogStore.open({
            type: DialogType.LearningConfirmPopup,
            props: {
                type: LearningConfirmPopupType.CONNECT_FAIL
            },
            confirm: {
                event: () => {}
            },
        })
    }

    const submitLevelAssessmentCurrentCode = () => {
        useLevelAssessmentService().submitAnswer(
            parseInt(route.params.levelId as string),
            parseInt(route.params.taskTemplateId as string),
            {
                action: "runCode",
                questionIdList: [parseInt(route.params.taskTemplateId as string)],
                submit: {
                    taskSolveTime: assessmentStore.taskSolveTime,
                    isLastQuestion: false,
                    output: "",
                    submissionAnswerList: [utf8ToBase64(learningStore.convertedCurrentCode)],
                    score: learningStore.score
                }
            }
        );
    }

    const submitProSkillAssureCurrentCode = () => {
        proSkillAssureService.requestAnswer(
            assessmentStore.proSkillAssure.assessmentId as number,
            learningStore.getCurrentProSkillAssureSet(learningStore.task.id as number).templateId as number,
            learningStore.task.id as number,
            ProSkillAssureAnswerCodeModel.create({
                action: ProSkillAssureAnswerCodeSubmitType.RUN,
                submit: ProSkillAssureAnswerCodeSubmitModel.create({
                    isLastQuestion: false,
                    output: learningStore.codeOutPut.join("\n"),
                    submissionAnswerList: [utf8ToBase64(learningStore.convertedCurrentCode)]
                })
            })).catch((e: unknown) => {
                // #2260: a dead exam session shows the session-expired popup; other failures as before.
                if (!useExamSession().handleUnauthorized(e)) throw e;
            });
    }

    return {
        initToken,
        onClickRun,
        socketOnClose,
        frontendCodeRun,
        saveCodeOnly,
        saveHistory
    };
}
